import classNames from 'classnames'
import React, { useCallback, useEffect, useState } from 'react'
import { AiOutlineClose } from 'react-icons/ai'
import Input, { InputProps } from '../Input'
import styles from './TagInput.module.scss'
import { ErrorMessage, Field } from 'formik'
import { FlexColumn } from 'components/common/Containers'

interface Props extends Omit<InputProps, 'onChange'> {
  trigger?: boolean
  wrapperClassName?: string
  tags: string[]
  onChange: (tags: string[]) => void
  isFormik?: boolean
  name?: string
  withErrorMessage?: boolean
  allowSpaces?: boolean
}

const TagInput: React.FC<Props> = ({
  wrapperClassName,
  tags,
  onChange,
  trigger,
  isFormik = false,
  name,
  withErrorMessage,
  allowSpaces = false,
  ...props
}) => {
  const [value, setValue] = useState('')

  const onChangeLocal = React.useCallback(
    (newTags: string[]) => {
      const search = newTags.join(',')
      window.history.replaceState(null, '', `?search=${search}`)
      onChange(newTags)
    },
    [onChange]
  )

  const handleAdd = useCallback(() => {
    if (value.trim()) {
      if (!tags.includes(value)) {
        onChangeLocal([...tags, value])
      }

      setValue('')
    }
  }, [tags, value, onChangeLocal])

  useEffect(() => {
    if (trigger) {
      handleAdd()
    }
  }, [trigger, handleAdd])

  return (
    <>
      {!isFormik ? (
        <div className={classNames(styles.inputWrapper, wrapperClassName)}>
          {tags.map((tag, index) => (
            <span key={index} className={styles.tag}>
              {tag}
              <AiOutlineClose
                className={styles.close}
                onClick={() => {
                  const newTags = [...tags]
                  newTags.splice(index, 1)

                  onChangeLocal(newTags)
                }}
              />
            </span>
          ))}

          <Input
            onBlur={handleAdd}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            isFormik={false}
            onKeyDown={(e) => {
              if (
                e.key === 'Enter' ||
                e.key === 'Tab' ||
                (allowSpaces ? e.key === ',' : e.key === ' ')
              ) {
                if (e.key !== 'Enter') e.preventDefault()
                handleAdd()
              }
            }}
            {...props}
          />
        </div>
      ) : (
        <FlexColumn gap="5px" className={wrapperClassName}>
          <Field name={name}>
            {({ field, form }: any) => (
              <div
                className={classNames(
                  styles.inputWrapper,
                  form.errors[name] && styles.error,
                  wrapperClassName
                )}
              >
                {tags.map((tag, index) => (
                  <span key={index} className={styles.tag}>
                    {tag}
                    <AiOutlineClose
                      className={styles.close}
                      onClick={() => {
                        const newTags = [...tags]
                        newTags.splice(index, 1)

                        onChangeLocal(newTags)
                      }}
                    />
                  </span>
                ))}

                <Input
                  name={name}
                  onBlur={handleAdd}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  isFormik={false}
                  onKeyDown={(e) => {
                    if (
                      e.key === 'Enter' ||
                      e.key === 'Tab' ||
                      (allowSpaces ? e.key === ',' : e.key === ' ')
                    ) {
                      if (e.key !== 'Enter') e.preventDefault()
                      handleAdd()
                    }
                  }}
                  {...props}
                />
              </div>
            )}
          </Field>
          {withErrorMessage && (
            <ErrorMessage
              className={classNames('error')}
              name={name}
              component="span"
            />
          )}
        </FlexColumn>
      )}
    </>
  )
}

export default TagInput
